import { initGpu, configure, GpuInitError, setPixelBudget } from './render/gpu';
import { Renderer } from './render/renderer';
import { World } from './game/world';
import { Input } from './core/input';
import { TouchInput, loadTouchMode, hasTouchHardware, type TouchMode } from './core/touch';
import { Hud } from './hud/hud';
import { Audio } from './audio/audio';
import { applyAA, detectQuality, isTouchDevice } from './core/quality';
import { SUN_PRESETS } from './game/config';
import { SkyTuner } from './hud/skyTuner';
import { loadDetailTextures } from './render/textures';
import { ENV_URL, ENV_BYTES } from './render/envMap';
import { buildHabKit, fetchHabKit } from './render/habKitMesh';
import { F_STOPS, loadSave, loadSettings, writeSettings, type AAPref, type TierPref } from './game/save';
import {
  androidNonChrome, chromeIntentUrl, goFullscreen, isHandheld, shouldGate,
  showInstallGate, watchForInstallPrompt,
} from './core/install';
import { loadProfile } from './game/profile';
import { Camera } from './game/camera';
import { MODES, MODE_LIST } from './game/modes';
import { bankSurvival, survivalOffer, survivalPick, survivalRun } from './game/survival';
import type { ModeRules } from './game/mode';

// Chrome fires `beforeinstallprompt` early and exactly once, so the listener has
// to exist before anything async happens or the install button has nothing left
// to call.
watchForInstallPrompt();

const boot = document.getElementById('boot')!;
const bootMsg = document.getElementById('boot-msg')!;

/**
 * Publish where boot got to, for `boot-trap.js`.
 *
 * A startup timeout used to report "this build needs WebGPU", which is a guess
 * and — on a phone reporting it — a wrong one: a browser with no `navigator.gpu`
 * throws `GpuInitError` and gets a correct message, so reaching the timeout at
 * all means the API was there and something AFTER it hung. Telling a player
 * their browser is unsupported when it is not sends them to fix the wrong thing
 * and tells us nothing. The phase is the one fact that makes the screenshot
 * worth having.
 */
function phase(text: string) {
  bootMsg.textContent = text;
  (window as unknown as { __ABYSS_PHASE?: string }).__ABYSS_PHASE = text;
}
const rotate = document.getElementById('rotate')!;

function fail(message: string) {
  boot.classList.remove('hidden');
  boot.innerHTML = `<h1>ABYSS</h1><p class="err">${message}</p>`;

  // The same facts the startup watchdog prints. A GPU failure went out with no
  // diagnostics at all, so a photograph of this screen said only that it had
  // failed — which is how two rounds of reports went by without establishing
  // whether the browser had even been identified correctly.
  const diag = document.createElement('p');
  diag.className = 'diag';
  const ua = navigator.userAgent;
  const brand = /YaBrowser|SamsungBrowser|EdgA|OPR|Firefox|MiuiBrowser|HuaweiBrowser/.exec(ua);
  const chrome = /Chrome\/(\d+)/.exec(ua);
  diag.textContent = [
    `webgpu: ${'gpu' in navigator ? 'present' : 'absent'}`,
    `browser: ${brand ? brand[0] : chrome ? `chrome ${chrome[1]}` : 'unknown'}`,
    `android: ${(/Android (\d+)/.exec(ua) ?? [, '?'])[1]}`,
    `display: ${window.innerWidth}x${window.innerHeight}`,
  ].join('  ·  ');
  boot.append(diag);

  if (!/Android/.test(ua)) return;

  const say = (text: string, cls = 'err') => {
    const el = document.createElement('p');
    el.className = cls;
    el.textContent = text;
    boot.append(el);
  };

  if (androidNonChrome()) {
    // A browser we recognise as lacking WebGPU. One instruction, one button.
    say('THIS BROWSER HAS NO WEBGPU. CHROME ON THIS PHONE DOES.');
    const open = document.createElement('a');
    open.className = 'gate-btn';
    open.textContent = 'OPEN IN CHROME';
    open.href = chromeIntentUrl();
    open.rel = 'noopener';
    boot.append(open);
    return;
  }

  // ALREADY IN CHROME, and it still would not produce an adapter.
  //
  // The previous copy read "IF THIS IS NOT CHROME, TRY CHROME", which is a
  // sentence that helps nobody: the player IS in Chrome, so it reads as the
  // game failing to recognise the browser rather than as advice — reported as
  // exactly that. Once `androidNonChrome` is false we know which browser this
  // is, and pretending otherwise wastes the one screen they will read.
  //
  // Chrome ships WebGPU on Android from 121, but only where the GPU and driver
  // are on its supported list, and it withholds it under Battery Saver. Those
  // are the three things a player can actually change, so those are the three
  // things listed. `chrome://` cannot be linked from a page — a link would do
  // nothing — so the flag is offered to copy instead.
  say('CHROME HAS WEBGPU, BUT WOULD NOT HAND OVER A GPU ON THIS PHONE.');
  say('· UPDATE CHROME — WEBGPU NEEDS 121 OR NEWER', 'diag');
  say('· TURN BATTERY SAVER OFF, THEN RELOAD', 'diag');
  say('· OR ENABLE THE FLAG BELOW AND RELAUNCH CHROME', 'diag');

  const FLAG = 'chrome://flags/#enable-unsafe-webgpu';
  const copy = document.createElement('button');
  copy.className = 'gate-btn';
  copy.type = 'button';
  copy.textContent = 'COPY FLAG ADDRESS';
  copy.addEventListener('click', async () => {
    try {
      await navigator.clipboard.writeText(FLAG);
      copy.textContent = 'COPIED — PASTE IN CHROME';
    } catch {
      // Clipboard needs permission it may not have. Show it to be typed.
      copy.textContent = FLAG;
    }
  });
  boot.append(copy);
}

/** CSS env() safe-area insets, so the HUD clears notches and home indicators. */
function safeAreas() {
  const s = getComputedStyle(document.documentElement);
  const px = (v: string) => parseFloat(s.getPropertyValue(v)) || 0;
  return { top: px('--sat'), bottom: px('--sab'), left: px('--sal'), right: px('--sar') };
}


/**
 * Fetch the baked environment probes.
 *
 * Eight seconds, then give up — the same limit and the same reasoning as the
 * detail textures: `fetch` does not time out on its own, and a request that
 * never settles parks boot on a phase message forever. A hang has to degrade
 * like a failure, because to the player it is one.
 *
 * Returns null on anything going wrong, which `buildEnvTexture` turns into flat
 * per-depth colours out of the inlined spherical harmonics.
 */
async function fetchEnvProbes(): Promise<ArrayBuffer | null> {
  try {
    const ctl = new AbortController();
    const timer = setTimeout(() => ctl.abort(), 8000);
    const res = await fetch(ENV_URL, { cache: 'force-cache', signal: ctl.signal });
    clearTimeout(timer);
    if (!res.ok) throw new Error(String(res.status));
    const buf = await res.arrayBuffer();
    if (buf.byteLength < ENV_BYTES) throw new Error(`short: ${buf.byteLength}`);
    return buf;
  } catch (err) {
    console.warn('[abyss] environment probes unavailable, using flat SH', err);
    return null;
  }
}

async function start() {
  // Before the GPU, the shaders or a single chunk of seabed: on a handheld that
  // has not installed the game, none of that should be paid for.
  if (shouldGate()) {
    showInstallGate();
    return;
  }

  const gfx = document.getElementById('gfx') as HTMLCanvasElement;
  const hudCanvas = document.getElementById('hud') as HTMLCanvasElement;

  phase('ACQUIRING GPU');
  const gpu = await initGpu(gfx);

  // Settings are read before the tier is chosen: a saved DETAIL preference has
  // to be in hand before the renderer is built around a tier.
  const settings = loadSettings();
  // Sample count and the post-AA pass are both fixed when the pipelines are
  // built, so the player's choice has to land before the renderer exists.
  const baseTier = detectQuality(gpu.adapter, settings.tier);
  const quality = applyAA(baseTier, settings.aa);
  /**
   * The tier's budget, times the player's RENDER SCALE. 0 means uncapped —
   * `setPixelBudget(Infinity)` leaves `configure` at the display's own DPR.
   */
  const applyRes = () => setPixelBudget(
    settings.gfxRes === 0 ? Infinity : quality.maxPixels * settings.gfxRes,
  );
  applyRes();
  /**
   * SEED THE GRAPHICS ROWS FROM THE TIER THE DEVICE ACTUALLY GOT.
   *
   * `gfxAo`, `gfxShadow`, `gfxSsr`, `gfxSky` and `gfxAf` are the one family of
   * settings that is NOT tier-aware: whatever `loadSettings` defaults them to
   * ships identically to a desktop and to a phone. That is why they sat at HIGH
   * with reflections at MEDIUM — numbers chosen to be survivable on the worst
   * device that would ever load the page, and therefore wrong on the machine
   * most people will actually play on.
   *
   * This is the only place that knows better, because the tier is not resolved
   * until `detectQuality` has seen the adapter. So it runs here rather than in
   * `save.ts`, behind the same `sv` revision the other one-shot migrations use:
   * it fires once, writes the result, and never overrides a choice the player
   * has made since.
   *
   * `high` is a desktop GPU and gets ULTRA across the board with 16x
   * anisotropy. `balanced` keeps HIGH. `mobile` drops to LOW, where the point
   * is a frame rate rather than a reflection.
   */
  if ((settings.sv ?? 1) < 4) {
    const lvl = quality.name === 'high' ? 4 : quality.name === 'balanced' ? 3 : 1;
    settings.gfxAo = lvl;
    settings.gfxShadow = lvl;
    settings.gfxSsr = lvl;
    settings.gfxSky = lvl;
    settings.gfxAf = quality.name === 'high' ? 16 : quality.name === 'balanced' ? 8 : 4;
    settings.sv = 4;
    writeSettings(settings);
  }
  console.info(`[abyss] quality tier: ${quality.name}`
    + ` · gfx ao/shadow/ssr/sky ${settings.gfxAo}/${settings.gfxShadow}`
    + `/${settings.gfxSsr}/${settings.gfxSky} · af ${settings.gfxAf}x`);

  phase('READING SURFACE DATA');
  const detail = await loadDetailTextures(gpu.device, settings.gfxAf);
  /**
   * THE ENVIRONMENT PROBES, AS A BINARY ASSET RATHER THAN A JS MODULE.
   *
   * Five 512 x 256 probes with mips is 7 MB. Inlined as base64 it was 9.3 MB of
   * string literal, which crashed esbuild's transform service outright — every
   * request to every file 500ing until the dev server was restarted — and took
   * a production build from 1.4 seconds to over three minutes.
   *
   * Fetched here, beside the detail textures, because it has the same shape of
   * problem: a large asset that must not be allowed to hang boot. Same timeout,
   * same degrade-to-a-fallback posture. A failure costs the probes their
   * structure, not the frame.
   */
  const envData = await fetchEnvProbes();
  /**
   * The habitat kit, baked from the Fusion CAD. Fetched here beside the other
   * two large assets and for the same reasons: too big to inline, and a hang
   * must degrade like a failure. Null falls back to the procedural builders.
   */
  const habRaw = await fetchHabKit();
  const habKit = habRaw ? buildHabKit(habRaw) : null;

  phase('COMPILING SHADERS');
  const renderer = new Renderer(gpu, quality, detail, envData);

  phase('GENERATING SEABED');
  const audio = new Audio();
  // The account is loaded before any world exists, because a world is built
  // around which boat you own. `let`, not `const`: a mode switch constructs a
  // fresh World rather than trying to reset forty mutable fields, which is
  // correct by construction and needs no test to believe.
  const profile = loadProfile();
  let world = new World(renderer, audio, quality, MODES.campaign!, profile, habKit);
  const hud = new Hud(hudCanvas);
  const input = new Input();

  /**
   * The one place mute is changed. The setting is the truth; the graph and the
   * HUD are told what it is. Previously each of the three call sites flipped
   * `settings.muted` by hand and separately asked the audio for the opposite of
   * whatever it currently held, so any path that ran one without the other left
   * the menu and the sound permanently disagreeing.
   */
  const applyMute = (on: boolean) => {
    settings.muted = audio.setMuted(on);
    // The HUD cannot read the audio graph, so the shell mirrors the state into
    // it — otherwise the pause menu always claims the sound is on.
    hud.muted = settings.muted;
  };
  applyMute(settings.muted);

  /**
   * A setting changed that only takes effect on a fresh renderer.
   *
   * Held until the player leaves OPTIONS rather than acted on immediately, so
   * that cycling a row is free and only the decision costs a restart.
   */
  let pendingReload = false;
  /** A dive was running when a reload-needing setting was changed. */
  let inDive = false;

  /**
   * Copy the settings the menu displays into the HUD.
   *
   * Called every frame a menu is open, not once at boot. The rows are built
   * from these mirrors, so a handler that changed `settings` without also
   * updating its mirror left the row showing the OLD value — which, once the
   * restart was deferred, meant cycling gave no feedback at all and you were
   * choosing one step behind the label. Refreshing the whole set from one
   * place makes that class of bug unreachable rather than fixed.
   */
  const syncSettings = () => {
    hud.frameCap = settings.frameCap;
    hud.musicVol = settings.musicVol;
    hud.sfxVol = settings.sfxVol;
    hud.ambVol = settings.ambVol;
    hud.tierPref = settings.tier;
    hud.hullPref = settings.hull;
    hud.dofStop = settings.dof;
    hud.ssfx = settings.ssfx;
    hud.ibl = settings.ibl;
    hud.brightness = settings.brightness;
    hud.gfxAo = settings.gfxAo;
    hud.gfxShadow = settings.gfxShadow;
    hud.gfxSsr = settings.gfxSsr;
    hud.gfxSky = settings.gfxSky;
    hud.gfxAf = settings.gfxAf;
    hud.gfxRes = settings.gfxRes;
    hud.todName = SUN_PRESETS.find((p) => p.id === settings.timeOfDay)?.name ?? '';
    hud.aaPref = settings.aa;
    hud.pendingReload = pendingReload;
    // Sampled while options is open, and held until the dive actually ends —
    // at which point the deferred reload is free and finally takes.
    if (hud.optionsInDive) inDive = true;
    else if (hud.screen === 'title' || hud.overTitle) inDive = false;
  };
  syncSettings();
  audio.setLevels({ music: settings.musicVol, sfx: settings.sfxVol, amb: settings.ambVol });
  // What AUTO resolves to on this machine — read from the tier BEFORE the
  // player's override is applied, so the row still says what AUTO means while
  // they are cycling through the values that are not AUTO.
  hud.aaActual = baseTier.taa ? 'TAA' : baseTier.samples > 1 ? 'MSAA 4x' : baseTier.fxaa ? 'FXAA' : 'OFF';
  renderer.dof = settings.dof;
  // Apply what was saved. Every one of these is read once per frame from the
  // object rather than the settings, so a setting that is never pushed across
  // here is a setting that silently only works until you reload.
  renderer.ssfx = settings.ssfx;
  renderer.ibl = settings.ibl;
  renderer.brightness = settings.brightness;
  renderer.gfxAo = settings.gfxAo;
  renderer.gfxShadow = settings.gfxShadow;
  renderer.gfxSsr = settings.gfxSsr;
  renderer.gfxSky = settings.gfxSky;
  world.setTimeOfDay(settings.timeOfDay);
  hud.tierActual = quality.name;

  // The on-screen stick is always wired up, but in 'auto' mode it stays
  // invisible and inert until a real finger touches the screen — a desktop
  // that merely reports touch points never sees it.
  const touch = new TouchInput(document.getElementById('frame') as HTMLElement);
  touch.mode = loadTouchMode();
  input.touch = touch;
  hud.touch = touch;

  const toggle = document.getElementById('touch-toggle') as HTMLButtonElement;
  const applyToggle = () => {
    const on = touch.mode !== 'off' && (touch.mode === 'on' || hasTouchHardware());
    toggle.dataset.on = on ? '1' : '0';
    toggle.textContent = touch.mode === 'on' ? 'TOUCH ON' : touch.mode === 'off' ? 'TOUCH OFF' : 'TOUCH AUTO';
    // Only offer the control where it could plausibly be wanted.
    toggle.classList.toggle('show', hasTouchHardware() || touch.mode !== 'auto');
  };
  const cycle = () => {
    const order: TouchMode[] = ['auto', 'on', 'off'];
    touch.setMode(order[(order.indexOf(touch.mode) + 1) % order.length]);
    applyToggle();
  };
  toggle.addEventListener('click', cycle);
  applyToggle();

  // Browsers only allow audio to start from a user gesture — and a phone can
  // park the context again whenever the app is backgrounded, so this stays
  // subscribed rather than firing once. touchend is included because iOS has
  // historically been the fussiest about which gesture counts.
  const wake = () => {
    audio.start();
    audio.resume();
    if (audio.running) {
      for (const ev of GESTURES) window.removeEventListener(ev, wake);
      // There is no AudioContext until a gesture, so this is the earliest the
      // score can begin. Whatever is on screen at that moment decides which
      // pool it draws from — usually the title.
      audio.music?.playFor(hud.screen === 'title' ? 'title' : 'dive');
    }
  };
  const GESTURES = ['pointerdown', 'pointerup', 'touchend', 'click', 'keydown', 'gamepadconnected'];
  for (const ev of GESTURES) window.addEventListener(ev, wake, { passive: true });
  // Coming back from the background can leave the context suspended.
  document.addEventListener('visibilitychange', () => {
    if (!document.hidden) audio.resume();
  });

  const resize = () => {
    if (configure(gpu)) renderer.resize();
    hud.resize();
    const sa = safeAreas();
    hud.safe = sa;
    touch.resize(hudCanvas.clientWidth, hudCanvas.clientHeight, sa.bottom, sa.right, sa.left, sa.top);
    // Portrait on a phone leaves no room for a side-on game.
    const portrait = hudCanvas.clientHeight > hudCanvas.clientWidth * 1.05;
    // And it goes away entirely once a menu is open, so the menu is READABLE
    // in portrait rather than being covered by a note about portrait.
    rotate.classList.toggle(
      'show', portrait && isTouchDevice() && hud.screen === 'none',
    );
  };
  window.addEventListener('resize', resize);
  window.addEventListener('orientationchange', () => setTimeout(resize, 150));
  resize();

  // Build the chunks around the spawn before the first frame is shown. Meshing
  // happens in a worker now, so this yields rather than spinning — and it gives
  // up after a moment rather than hanging the boot if the worker never answers.
  const spawnX = world.player.x;
  const deadline = performance.now() + 6000;
  while (!world.terrain.readyAround(spawnX, 3) && performance.now() < deadline) {
    world.terrain.update(spawnX, 4);
    await new Promise((r) => setTimeout(r, 8));
  }

  // The sky tuner. Every question about the sky in the last few days ended in
  // "a bit more hue" rather than a number, because the numbers were in a shader
  // and the picture was on screen and the two were a rebuild apart. This puts
  // them in the same place.
  const skyTuner = new SkyTuner(
    renderer,
    SUN_PRESETS.map((p) => ({ id: p.id, name: p.name })),
    (id) => {
      settings.timeOfDay = id;
      world.setTimeOfDay(id);
      hud.todName = SUN_PRESETS.find((p) => p.id === id)?.name ?? '';
      writeSettings(settings);
    },
    () => settings.timeOfDay,
  );

  // A legend for the chart above. Plain DOM in the row's own order — the names
  // come off `world.probeLabels`, so the strip cannot drift from what is drawn.
  const probeLegend = document.createElement('div');
  // `hidden` would be the obvious way to do this and does not work: it is a UA
  // rule setting `display:none`, and the inline `display:flex` below outranks
  // it, so the strip would sit there permanently with the chart switched off.
  probeLegend.style.cssText = 'position:fixed;left:50%;bottom:10px;transform:translateX(-50%);'
    + 'z-index:35;display:none;gap:14px;padding:5px 12px;pointer-events:none;'
    + 'font:11px/1.4 ui-monospace,SFMono-Regular,Menlo,monospace;letter-spacing:.09em;'
    + 'color:#bfe6f2;background:rgba(6,18,26,.72);border:1px solid rgba(120,200,220,.26);'
    + 'border-radius:4px';
  for (const name of world.probeLabels) {
    const cell = document.createElement('span');
    cell.textContent = name;
    probeLegend.appendChild(cell);
  }
  document.body.appendChild(probeLegend);

  // Which rung of the F7 ablation ladder is in force. Each step is cumulative:
  // the point is a running total, not five independent switches.
  let ablate = 0;
  /** So the ladder's first rung can put depth of field back where it found it. */
  const dofWas = renderer.dof;
  /** Which rung of the F8 resolution ladder is in force. */
  let scale = 0;

  window.addEventListener('keydown', (e) => {
    if (e.code === 'F3' || e.code === 'Backquote') hud.showDebug = !hud.showDebug;
    if (e.code === 'KeyK' && !e.metaKey && !e.ctrlKey) skyTuner.toggle();
    // Cycle the volumetric march. It is the largest single item in the frame
    // and the one whose look is most a matter of taste, so it gets a key: a
    // shorter march is chunkier and more present, a longer one subtler and
    // several milliseconds dearer, and the only way to choose is to see both.
    // THE MATERIAL CHART. Eight balls that ride with the boat, so a lighting
    // change can be walked from the surface to the abyss and read off directly
    // instead of inferred from what a rock looked like.
    if (e.code === 'F2') {
      e.preventDefault();
      world.showProbes = !world.showProbes;
      probeLegend.style.display = world.showProbes ? 'flex' : 'none';
      world.showToast('MATERIAL CHART', world.showProbes ? 'ON' : 'OFF', '#5fe4e0');
    }
    // ABLATION, because this adapter has no timestamp-query and the F3 line
    // therefore files the entire GPU frame as "unaccounted". With no way to ask
    // where the time goes, the only honest instrument left is to turn one thing
    // off and read the counter — so each press removes exactly one subsystem and
    // says which, and the frame time between two presses is that subsystem's
    // cost. Cheaper to build than a guess and it cannot lie.
    if (e.code === 'F7') {
      e.preventDefault();
      const steps: [string, () => void][] = [
        ['EVERYTHING ON', () => {
          renderer.ssfx = true; renderer.gfxSsr = 3; renderer.gfxAo = 3;
          renderer.gfxShadow = 3; renderer.gfxSky = 3; renderer.seaSurface = true;
          renderer.probe.wake = true; renderer.probe.particles = true;
          renderer.probe.bloom = true; renderer.dof = dofWas;
          renderer.probe.instances = true; renderer.probe.instanceUpload = true;
        }],
        ['NO REFLECTIONS', () => { renderer.gfxSsr = 0; }],
        ['NO OCCLUSION', () => { renderer.gfxAo = 0; }],
        ['NO CONTACT SHADOWS', () => { renderer.gfxShadow = 0; }],
        // Separately, because they are not the same thing and were measured as
        // one: the sky is a gradient and some noise, the sea is a per-pixel
        // march into a displaced height field with a reflection on top.
        ['NO SKY', () => { renderer.gfxSky = 0; }],
        ['NO SEA SURFACE', () => { renderer.seaSurface = false; }],
        // THE PASSES NO RESOLUTION CHANGE CAN REACH.
        //
        // A tiny window still costing 69 percent says the expensive part is
        // not pixels, and these four are where the fixed work is. The wake is
        // the first suspect by a distance: 23 render passes a frame — one
        // advect, one divergence, sixteen pressure sweeps and a gradient — on a
        // target with a 48x48 FLOOR, so shrinking the window takes away the
        // texels and leaves every one of the passes. On a tile-based GPU the
        // per-pass setup and flush is the cost, not the fill.
        ['NO WAKE (23 passes)', () => { renderer.probe.wake = false; }],
        ['NO PARTICLES', () => { renderer.probe.particles = false; }],
        ['NO DEPTH OF FIELD', () => { renderer.dof = 0; }],
        ['NO BLOOM', () => { renderer.probe.bloom = false; }],
        // AND THE LAST THING THAT IS THE SAME SIZE WHATEVER THE WINDOW IS.
        //
        // A hundred thousand instances at 208 bytes each, re-sent every frame:
        // 20.8 MB, through a `writeBuffer` that stages it, so the CPU writes it
        // and the GPU copies it. Two rungs, because the draws and the upload
        // are different costs — the gap between them is the bus.
        ['NO MESH DRAWS', () => { renderer.probe.instances = false; }],
        ['NO INSTANCE UPLOAD', () => { renderer.probe.instanceUpload = false; }],
      ];
      ablate = (ablate + 1) % steps.length;
      steps[ablate]![1]();
      world.showToast('ABLATION', steps[ablate]![0], '#ffb347');
    }
    // AND THE ONE QUESTION THE LADDER ABOVE CANNOT ANSWER.
    //
    // "Everything off and the GPU is still 83 percent busy" says the cost is
    // not in any single effect — which leaves the fixed chain: a multi-target
    // scene pass, then a composite, a temporal resolve, a sharpen and an FXAA,
    // every one of them reading and writing the full frame. That is bandwidth,
    // and bandwidth scales with PIXELS, not with what the shader does per pixel.
    //
    // This settles it in ten seconds. If 70 percent of the resolution nearly
    // doubles the frame rate, the frame is fill-bound and the answer is
    // resolution — render smaller, upscale, spend the savings on the effects.
    // If it barely moves, the cost is per-pixel work after all and the ladder
    // is the right tool. One of those two is true and guessing between them has
    // already cost more than measuring will.
    if (e.code === 'F8') {
      e.preventDefault();
      // The same field the slider writes, not a second path to the same
      // setting. Two controls for one value is how they end up disagreeing.
      const steps = [1.0, 0.8, 0.65, 0.5];
      scale = (scale + 1) % steps.length;
      const f = steps[scale]!;
      renderer.skyTune.pixels = (quality.maxPixels * f * f) / 1_000_000;
      world.showToast('RENDER MEGAPIXELS',
        renderer.skyTune.pixels.toFixed(2), '#ffb347');
    }
    // THE ONE MEASUREMENT THAT WANTED ISOLATING, NOT LADDERING.
    //
    // The F7 ladder is cumulative, so its last rung reads "everything off AND
    // no upload" — which cannot tell you what the upload costs while the game
    // is otherwise running normally. That is the number worth having, so it
    // gets a key of its own and toggles nothing else.
    //
    // The world FREEZES while it is off: the instance buffers keep whatever
    // was in them at the moment of the last upload, so scenery and actors stop
    // moving and the boat stops steering. That is expected, it is the point,
    // and everything else in the frame carries on being drawn at full cost.
    if (e.code === 'F9') {
      e.preventDefault();
      renderer.probe.instanceUpload = !renderer.probe.instanceUpload;
      world.showToast('INSTANCE UPLOAD',
        renderer.probe.instanceUpload ? 'ON' : 'OFF  (world frozen)', '#ffb347');
    }
    if (e.code === 'F4') {
      const steps = [0.25, 0.4, 0.7, 1];
      const at = steps.findIndex((v) => Math.abs(v - renderer.probe.volumetric) < 0.01);
      renderer.probe.volumetric = steps[(at + 1) % steps.length];
      world.showToast(
        'VOLUMETRIC MARCH',
        `${Math.round(renderer.probe.volumetric * 100)}% OF STEPS`,
        '#5fe4e0',
      );
    }
    if (e.code === 'KeyM') {
      applyMute(!settings.muted);
      writeSettings(settings);
    }
    // Camera tilt, on a key because it is a question about how it looks and the
    // only way to answer that is to flick between them in the same water.
    if (e.code === 'KeyV') {
      const mode = world.camera.cycleView();
      const deg = Camera.TILT_DEGREES[mode] ?? 0;
      world.showToast('VIEW', deg ? `TILT ${deg}°` : 'SIDE ON', '#5fe4e0');
    }
    // T was the keyboard alias for the touch-mode toggle; that has moved to Y,
    // and the on-screen button it shadows is unchanged.
    if (e.code === 'KeyY') cycle();
    // Free travel between lanes, and only with the instruments up.
    //
    // The game's way into a far lane is a gate you have to find; this key walks
    // into the room without finding it, which makes the one piece of content
    // that has to be discovered available to anyone who leans on the keyboard.
    // It is a tuning shortcut, so it lives where the other tuning lives —
    // behind F3 — rather than one keystroke from a first playthrough.
    //
    // It is also the ONLY caller that leaves `updateLane` to ease across, since
    // gates snap (`World.gates`). So it is the only way to reach the water
    // between slabs, where the sub is in no lane at all: weapons stow there and
    // every actor shades as unreachable, both of which are worth being able to
    // see on purpose.
    if (e.code === 'KeyT' && hud.showDebug) {
      const lane = world.travelNext();
      world.showToast('LANE', lane === 0 ? 'NEAR' : lane === 1 ? 'MIDDLE' : 'FAR', '#5fe4e0');
    }
    // THE HABITAT KIT. Cycle the authored bases (docs/HABITAT.md §3 Phase 0).
    // Same reasoning as the lane key above: §10's success test is a question
    // about how many pixels a silhouette gets, so it has to be asked in the
    // water rather than in CAD, and flying between four bases by hand is
    // minutes of travel per answer. Shift+B steps back.
    //
    // B, not H: H is bound to `hold` in input.ts and was toggling the ore
    // display instead.
    // NOT gated on F3. The lane key above is a gameplay shortcut that should
    // stay out of a first playthrough; this one only teleports between authored
    // set dressing, and requiring the instruments first just meant it silently
    // did nothing.
    if (e.code === 'KeyB') {
      world.showToast('HABITAT', world.gotoHabitat(e.shiftKey ? -1 : 1), '#5fe4e0');
    }
  });

  registerServiceWorker();

  // Debug handle: `ABYSS.world.player.x = 1300` etc. Handy for tuning.
  // Re-seated on every mode switch — a stale handle would silently point at a
  // dead World and every number read out of it would be a lie.
  const seatDebug = () => {
    (window as unknown as { ABYSS: unknown }).ABYSS = {
      world, renderer, input, hud, audio, gpu, quality, profile,
      // Survival's live state, for tuning a wave table without playing to it.
      // `ABYSS.survival.pick(id)` takes a card; `.run()` is the whole run.
      survival: {
        run: survivalRun,
        offer: survivalOffer,
        pick: (id: string) => survivalPick(world, id),
      },
      // `ABYSS.play('survival')` drops straight into a mode without the menu.
      play: (id: string) => {
        const r = MODES[id as keyof typeof MODES];
        if (!r) return `no such mode: ${Object.keys(MODES).join(', ')}`;
        if (r.id !== world.rules.id) enterMode(r);
        world.newRun();
        r.begin?.(world);
        beginRun();
        return r.label;
      },
    };
  };
  seatDebug();

  /**
   * Swap modes by building a new world. The alternative is resetting ~40
   * mutable fields on the old one and hoping none was missed; a survivor like
   * `pendingCarve` would invalidate an x-span belonging to the previous mode.
   */
  const enterMode = (rules: ModeRules) => {
    world = new World(renderer, audio, quality, rules, profile, habKit);
    seatDebug();
    return world;
  };

  // The run does not begin until the player picks something off the title.
  hud.openScreen('title');
  let sawVictory = false;
  let sawEnd = false;

  const beginRun = () => {
    hud.openScreen('none');
    // Seeded from the world, never blanked.
    //
    // KEEP EXPLORING routes through here, so blanking it re-opened the victory
    // card on the very next frame — `world.victory` is still true, and it is
    // supposed to be. The player could not leave the screen at all. `newRun`
    // clears the flag itself, so a fresh dive still starts at false.
    sawVictory = world.victory;
    sawEnd = false;
    // A track whose role is `both` keeps playing straight through this, so the
    // title does not slam into a restart the moment a dive begins.
    audio.music?.playFor('dive');
  };

  // Park the audio graph with it. A suspended context stops rendering the
  // drone, the ambience and the thruster rather than rendering them silently.
  document.addEventListener('visibilitychange', () => {
    if (document.hidden) audio.suspend();
    else audio.resume();
  });

  let last = performance.now();
  let fps = 60;
  // Rolling window for the periodic performance sample below.
  let perfAcc = 0, perfCount = 0, perfSum = 0, perfWorst = 0;
  let started = false;

  // The render-resolution slider, applied where `gpu` and `hud` are in scope.
  // Compared rather than subscribed, for the same reason the sun offsets are:
  // the panel writes plain numbers into `skyTune` and has nobody to call.
  let hudWasDrawn = true;
  let pixelsUsed = renderer.skyTune.pixels;
  let hudPixelsUsed = renderer.skyTune.hudPixels;
  hud.pixelBudget = hudPixelsUsed * 1_000_000;
  hud.resize();

  const loop = (now: number) => {
    if (renderer.skyTune.pixels !== pixelsUsed) {
      pixelsUsed = renderer.skyTune.pixels;
      setPixelBudget(pixelsUsed * 1_000_000);
      if (configure(gpu)) renderer.resize();
      hud.resize();
    }
    if (renderer.skyTune.hudPixels !== hudPixelsUsed) {
      hudPixelsUsed = renderer.skyTune.hudPixels;
      hud.pixelBudget = hudPixelsUsed * 1_000_000;
      hud.resize();
    }
    // Re-arm first, so a frame skipped by the cap below does not end the loop.
    requestAnimationFrame(loop);

    // NOTHING RUNS WHILE THE TAB IS HIDDEN.
    //
    // Browsers throttle `requestAnimationFrame` in a background tab but do not
    // reliably stop it, so a hidden game carried on simulating and drawing
    // frames nobody could see — on a laptop that is heat spent on nothing, and
    // heat is the one thing that makes the frame rate worse AFTER you come
    // back. `last` is kept current so the first visible frame gets an ordinary
    // dt instead of the 0.05 clamp, which is what made returning lurch.
    if (document.hidden) { last = now; return; }

    // Frame ceiling. The tolerance matters: asking for 60 on a 60 Hz display
    // means frames arrive at 16.67 ms with a little jitter either side, and a
    // threshold set exactly there would reject every other one and serve 30.
    if (settings.frameCap > 0 && now - last < 1000 / settings.frameCap - 2) return;

    // Clamp dt so an alt-tab or a chunk build never teleports the sub.
    const dt = Math.min(0.05, Math.max(1 / 1000, (now - last) / 1000));
    last = now;
    fps = fps * 0.9 + (1 / dt) * 0.1;

    input.update(dt);

    if (hud.screen !== 'none') {
      // A screen is up: the simulation is frozen, but the water keeps moving
      // behind it, which is what stops a pause feeling like a crash. The title
      // card goes further and runs the world as an attract loop, so the first
      // thing anyone sees is the game rather than a painted gradient.
      // Nothing is driving the boat, so it must not be holding anything. Every
      // frame rather than on the edge: see `releaseControls` — an edge can be
      // missed by a path nobody thought of, and a level test cannot.
      world.releaseControls();
      if (hud.screen === 'title' || hud.overTitle) world.attract(dt);
      // Before the rows are rebuilt, not after: the labels are drawn from these.
      syncSettings();
      const cmd = hud.updateMenu(input, world, dt);
      switch (cmd) {
        case 'pickCard':
          survivalPick(world, hud.pickedCard);
          beginRun();
          break;
        case 'pickMode': {
          const rules = MODES[hud.pickedMode] ?? MODES.campaign!;
          // A different mode is a different world; the campaign is the only one
          // that reuses the one already standing, and even then only to keep
          // `newRun`'s crater reset and save wipe in one place.
          if (rules.id !== world.rules.id) enterMode(rules);
          world.newRun();
          // A mode that needs to set a run up gets one call, after the world
          // exists and after newRun has cleared it.
          rules.begin?.(world);
          beginRun();
          break;
        }
        case 'newgame':
          world.newRun();
          beginRun();
          break;
        case 'continue': {
          // INTO THE MODE THAT WROTE IT. The save carries its own mode now, so
          // resuming is "put the world back the way it was" rather than "pour
          // the last run into whatever rules happen to be loaded" — which is
          // what made a descent unreachable once an expedition had been
          // started, with nothing anywhere saying so.
          const want = hud.resumeMode || world.rules.id;
          const s = loadSave(want);
          if (s) {
            const rules = MODE_LIST.find((r) => r.id === want);
            if (rules && rules.id !== world.rules.id) enterMode(rules);
            world.applySave(s);
          }
          beginRun();
          break;
        }
        case 'restart':
          // A survival run restarts; a campaign death respawns at the last
          // station. RETRY means different things and they are not close.
          if (!world.rules.persists) {
            world.newRun();
            world.rules.begin?.(world);
          } else {
            world.respawn();
          }
          beginRun();
          break;
        case 'resume':
          beginRun();
          break;
        case 'title':
          world.save();
          hud.openScreen('title');
          audio.music?.playFor('title');
          break;
        case 'toggleMute':
          applyMute(!settings.muted);
          writeSettings(settings);
          break;
        // Five steps rather than a slider: a list is navigable with a stick, a
        // key and a thumb without inventing a third input idiom for one screen.
        case 'cycleMusic':
        case 'cycleSfx':
        case 'cycleAmb': {
          const steps = [0, 0.25, 0.5, 0.75, 1];
          const key = cmd === 'cycleMusic' ? 'musicVol' : cmd === 'cycleSfx' ? 'sfxVol' : 'ambVol';
          const i = steps.findIndex((v) => Math.abs(v - settings[key]) < 0.01);
          settings[key] = steps[(i + 1) % steps.length];
          audio.setLevels({ music: settings.musicVol, sfx: settings.sfxVol, amb: settings.ambVol });
          writeSettings(settings);
          break;
        }
        case 'cycleCap': {
          const steps = [30, 60, 120, 0];
          settings.frameCap = steps[(steps.indexOf(settings.frameCap) + 1) % steps.length];
          writeSettings(settings);
          break;
        }
        case 'cycleTier': {
          const steps: TierPref[] = ['auto', 'mobile', 'balanced', 'high'];
          settings.tier = steps[(steps.indexOf(settings.tier) + 1) % steps.length];
          writeSettings(settings);
          // The renderer is built around a tier — sample counts, target sizes
          // and the specialised post shader are all fixed at construction — so
          // this is a reload rather than a live swap. DEFERRED, though: reloading
          // on every press meant walking back into OPTIONS to reach the next
          // value, so comparing three of them cost three restarts and four trips
          // through the menu. It happens once, on the way out.
          pendingReload = true;
          break;
        }
        case 'cycleAA': {
          const steps: AAPref[] = ['auto', 'off', 'fxaa', 'msaa', 'taa'];
          settings.aa = steps[(steps.indexOf(settings.aa) + 1) % steps.length]!;
          writeSettings(settings);
          // Same deferral as the tier: cycle freely, restart once on the way out.
          pendingReload = true;
          break;
        }
        case 'cycleTod': {
          // Live. The sun is rebuilt from the preset on the spot and every
          // frame after reads it, so the whole scene relights while you watch —
          // which is the only way to choose between nine of them.
          const ids = SUN_PRESETS.map((p) => p.id);
          const at = (ids.indexOf(settings.timeOfDay) + 1) % ids.length;
          settings.timeOfDay = ids[at]!;
          world.setTimeOfDay(settings.timeOfDay);
          writeSettings(settings);
          break;
        }
        case 'cycleAo':
        case 'cycleShadow':
        case 'cycleSky':
        case 'cycleSsr': {
          const key = cmd === 'cycleAo' ? 'gfxAo'
            : cmd === 'cycleShadow' ? 'gfxShadow'
              : cmd === 'cycleSky' ? 'gfxSky' : 'gfxSsr';
          settings[key] = (settings[key] + 1) % 5;
          renderer[key] = settings[key];
          hud[key] = settings[key];
          writeSettings(settings);
          break;
        }
        case 'cycleRes': {
          // LIVE, no reload. `configure` reads the module-level budget every
          // time it runs, so setting it and resizing is the whole operation —
          // the swap chain, every render target and the HUD all follow.
          const steps = [1, 1.5, 2, 3, 0];
          const at = steps.indexOf(settings.gfxRes);
          settings.gfxRes = steps[(at + 1) % steps.length]!;
          hud.gfxRes = settings.gfxRes;
          applyRes();
          resize();
          writeSettings(settings);
          break;
        }
        case 'cycleAf': {
          // A VALUE LIST, NOT A LEVEL, and a reload rather than a live change.
          //
          // The other four `gfx` rows are 0..4 and take effect on the next
          // frame because they only change a loop's step count in a uniform.
          // This one is baked into a GPUSampler at creation, and the sampler is
          // inside bind groups that were built around it — so the honest way to
          // change it is the way TIER and AA change, by rebuilding.
          const steps = [1, 2, 4, 8, 16];
          const at = steps.indexOf(settings.gfxAf);
          settings.gfxAf = steps[(at + 1) % steps.length]!;
          hud.gfxAf = settings.gfxAf;
          pendingReload = true;
          writeSettings(settings);
          break;
        }
        case 'cycleBright': {
          const steps = [0.7, 0.85, 1, 1.15, 1.3, 1.6];
          const at = steps.findIndex((v) => Math.abs(v - settings.brightness) < 0.01);
          settings.brightness = steps[(at + 1) % steps.length]!;
          renderer.brightness = settings.brightness;
          hud.brightness = settings.brightness;
          writeSettings(settings);
          break;
        }
        case 'cycleIbl': {
          settings.ibl = !settings.ibl;
          renderer.ibl = settings.ibl;
          hud.ibl = settings.ibl;
          writeSettings(settings);
          break;
        }
        case 'openTuner': {
          // Opens over whatever is on screen, menu included: the whole value of
          // it is watching the sky change while you drag, and a tuner that
          // needs you to close the menu first cannot show you the sky.
          skyTuner.toggle();
          break;
        }
        case 'cycleSsfx': {
          // Live, no reload: it is a uniform the composite reads each frame,
          // which is also why it is worth having as a switch at all — the
          // player can see what it buys by flipping it while looking at it.
          settings.ssfx = !settings.ssfx;
          renderer.ssfx = settings.ssfx;
          hud.ssfx = settings.ssfx;
          writeSettings(settings);
          break;
        }
        case 'cycleDof': {
          // OFF, then the ring from wide open to stopped down, then OFF again.
          const at = F_STOPS.indexOf(settings.dof);
          settings.dof = settings.dof === 0
            ? F_STOPS[0]!
            : at >= F_STOPS.length - 1 ? 0 : F_STOPS[at + 1]!;
          // No reload: the pass and the target are already there, and the
          // renderer skips them when this is zero.
          renderer.dof = settings.dof;
          writeSettings(settings);
          break;
        }
        case 'cycleHull': {
          // Three hulls, cycled newest-first. The two older boats are kept
          // rather than retired: same origin, axis and length, so this is the
          // only way to judge the new one against what it replaced.
          settings.hull = settings.hull === 'mk2' ? 'model'
            : settings.hull === 'model' ? 'classic' : 'mk2';
          writeSettings(settings);
          // The hull mesh is registered once, when the world is constructed, so
          // swapping it is a reload for the same reason the tier is.
          pendingReload = true;
          break;
        }
      }
    } else {
      if (input.state.pausePressed) {
        hud.openScreen('paused');
      } else if (input.state.mapPressed) {
        hud.openScreen('map');
      } else if (input.state.dockPressed && world.dockable) {
        world.dock(world.dockable);
        hud.openScreen('shop');
      } else {
        world.touchControls = touch.enabled;
        const tU = performance.now();
        world.update(input, dt);
        world.noteCpu('update', performance.now() - tU);
        // Arriving at a station for the first time opens the refit on its own.
        // Making the player discover a menu key is how upgrades go unused.
        if (world.pendingRefit) {
          world.pendingRefit = false;
          hud.openScreen('shop');
        }
        // A run card on offer holds the intermission open until it is taken.
        if (survivalOffer().length && !world.rules.persists) hud.openScreen('cards');
        if (world.victory && !sawVictory) {
          sawVictory = true;
          hud.openScreen('victory');
        }
        // A mode with an ending of its own. This is what finally makes the
        // 'gameover' screen and the 'restart' command reachable — both have
        // been dead code since they were written.
        if (world.rules.ended?.(world) && !sawEnd) {
          sawEnd = true;
          bankSurvival(world);
          hud.openScreen('gameover');
        }
      }
    }

    // Left OPTIONS with something that needs a fresh renderer. Settings are
    // already written, so the reload comes back to exactly what was chosen.
    // ONLY WHEN THERE IS NO DIVE TO INTERRUPT.
    //
    // These three settings genuinely need a rebuilt renderer, and that is a
    // page reload. From the title that is free. Mid-dive it saves, drops the
    // player to the title and makes them press CONTINUE — which is precisely
    // the round trip that hiding these rows from the pause screen was meant to
    // avoid, and precisely the round trip that showing them there is meant to
    // remove. So mid-dive the choice is simply stored, the row says NEXT
    // LAUNCH, and the flag is left set for whenever the dive ends.
    if (pendingReload && hud.screen !== 'options' && !inDive) {
      pendingReload = false;
      world.save();
      location.reload();
      return;
    }

    const aspect = gpu.width / gpu.height;
    const tF = performance.now();
    const frame = world.buildFrame(aspect);
    const tD = performance.now();
    // The refit's turntable. The HUD decides where it goes and the world puts
    // the hull there; neither needs to know about the other's units.
    //
    // One frame behind, because the HUD draws after the world and so the rect
    // in hand is last frame's. That is invisible for a panel that does not
    // move, and the alternative is laying the menu out twice.
    world.showcase = hud.modelRect;
    world.showcaseOre = hud.showcaseOre;
    world.spinShowcase(dt);
    world.draw(hud.screen === 'title');
    const tE = performance.now();
    world.noteCpu('frame', tD - tF);
    world.noteCpu('draw', tE - tD);

    // THE PERFORMANCE READOUT, ON THE GPU, AND IT HAS TO BE HERE.
    //
    // `world.draw` resets the text batch on its first line and `renderer.render`
    // flushes it on its last, so this gap is the entire window in which
    // anything can be added to it. Before `draw` is wiped; after `render` is
    // too late.
    //
    // It is drawn there rather than on the HUD canvas because it is the one
    // panel whose own cost falsifies what it reports — see `Hud.debugText`.
    if (renderer.probe.hud) {
      hud.debugText(world, renderer.text, gpu.width / gpu.dpr, gpu.height / gpu.dpr);
    }

    const ext = world.camera.halfExtent();
    renderer.render(frame, world.visibleChunks(), world.camera.viewY() > -260, {
      halfW: ext.w, halfH: ext.h, dt,
      flowX: 0.35, flowY: -0.05,
      px: world.player.x, py: world.player.y, speed: world.player.speed,
    });

    const tH = performance.now();
    world.noteCpu('render', tH - tE);
    // Skipped rather than drawn-and-hidden: the point of the switch is the
    // cost, and a cleared canvas the browser still composites is not it.
    if (renderer.probe.hud) hud.draw(world, fps);
    else if (hudWasDrawn) hud.clear();
    hudWasDrawn = renderer.probe.hud;
    world.noteCpu('hud', performance.now() - tH);

    // PERFORMANCE, RECORDED RATHER THAN REMEMBERED.
    //
    // The debug panel on F3 shows all of this live, and a screenshot of it
    // catches one instant — which is the wrong instant, because what makes a
    // mode feel bad is the worst frames, not the average one. So a sample goes
    // into the telemetry log every few seconds carrying BOTH: the mean frame
    // and the slowest one in the window, next to the counts that explain it.
    //
    // There is no GPU timer to read. WebGPU exposes timestamp queries only
    // behind an optional feature this build does not request, so `gpu` here is
    // the honest proxy: the frame time left over once the CPU's own measured
    // work is subtracted. When that number is large and `upd` is small, the
    // answer is fill and triangles; when it is the other way round, it is us.
    perfCount++;
    perfSum += dt;
    if (dt > perfWorst) perfWorst = dt;
    perfAcc += dt;
    if (perfAcc > 6 && hud.screen === 'none') {
      const s = world.drawStats;
      const c = world.cpu;
      const cpuMs = c.update + c.frame + c.draw + c.hud;
      world.telemetry('perf', {
        mode: world.rules.id,
        fps: Math.round(perfCount / perfSum),
        worstMs: perfWorst * 1000,
        cpuMs,
        upd: c.update, draw: c.draw, hud: c.hud, lamp: c.lamp,
        gpuMs: Math.max(0, (perfSum / perfCount) * 1000 - cpuMs),
        tris: s.terrainTris + s.instTris,
        inst: s.instTris,
        draws: s.terrainDraws + s.instDraws,
        // Capacity, not a live count: dead particles are clipped in the vertex
        // stage rather than skipped, so this is what the frame always draws.
        partCap: s.particles,
        lights: s.lights,
        dropInst: s.slotOverflow,
        dropLight: s.lightOverflow,
        enemies: world.actors.enemies.reduce((n, e) => n + (e.alive ? 1 : 0), 0),
        shots: world.actors.projectiles.reduce((n, q) => n + (q.alive ? 1 : 0), 0),
      });
      world.flushTelemetry();
      perfAcc = 0; perfCount = 0; perfSum = 0; perfWorst = 0;
    }

    if (!started) {
      started = true;
      // Stands the boot fault trap down: from here an error is a bug to fix,
      // not a reason to tear down a running game.
      (window as unknown as { __ABYSS_READY: boolean }).__ABYSS_READY = true;
      boot.classList.add('hidden');
      setTimeout(() => boot.remove(), 900);

      // Take fullscreen on the player's first touch or click. The manifest asks
      // for `standalone` now rather than `fullscreen` — the latter is the least
      // honoured display value and some Android browsers drop all the way to a
      // browser tab rather than falling back through it — so the fullscreen it
      // used to buy is claimed here instead, where a gesture makes it legal.
      // Once: `goFullscreen` is idempotent but re-asking on every tap would
      // fight a player who deliberately left it.
      if (isHandheld()) {
        const grab = () => goFullscreen();
        window.addEventListener('pointerdown', grab, { once: true });
        window.addEventListener('touchend', grab, { once: true });
      }
    }
  };

  // Save on the way out — a phone that gets killed in the background should not
  // cost the player the station they just reached.
  const flush = () => {
    if (hud.screen !== 'title' && world.lastStation) world.save();
    // Diagnostics, written on the same beat. It is what the first real
    // playthrough is supposed to produce, and a phone killed in the background
    // is exactly the run most worth not losing the record of.
    world.flushTelemetry();
  };
  window.addEventListener('pagehide', flush);
  document.addEventListener('visibilitychange', () => {
    if (document.hidden) flush();
  });

  requestAnimationFrame(loop);
}

/**
 * Installs the worker and, when a new one is waiting, offers the update rather
 * than taking it. Activating eagerly would swap the bundle out from under a
 * player mid-dive.
 */
function registerServiceWorker() {
  if (!('serviceWorker' in navigator) || location.protocol === 'http:') return;
  const chip = document.getElementById('update') as HTMLButtonElement | null;

  navigator.serviceWorker.register('/sw.js').then((reg) => {
    const offer = (worker: ServiceWorker | null) => {
      if (!worker || !chip || !navigator.serviceWorker.controller) return;
      chip.classList.add('show');
      chip.onclick = () => {
        worker.postMessage('skip-waiting');
        chip.textContent = 'UPDATING…';
      };
    };
    if (reg.waiting) offer(reg.waiting);
    reg.addEventListener('updatefound', () => {
      const w = reg.installing;
      w?.addEventListener('statechange', () => {
        if (w.state === 'installed') offer(reg.waiting);
      });
    });
  }).catch((err) => console.warn('[abyss] service worker registration failed', err));

  let reloading = false;
  navigator.serviceWorker.addEventListener('controllerchange', () => {
    if (reloading) return;
    reloading = true;
    location.reload();
  });
}

start().catch((err) => {
  console.error(err);
  fail(
    err instanceof GpuInitError
      ? err.message
      : `Startup failed: ${err?.message ?? err}`,
  );
});
